import { useFetcher } from 'react-router';
import type {
    TagData,
    TagDataUseToCreateTag,
} from '@modules/product-tagging/type';
import type { TagPosition } from '@modules/product-tagging/type';
import { getPercentageValue, getPixelValue, roundToTwo } from '~/utils/math';
import { useProductTagging } from '../context';
import { useEffect } from 'react';
import type { Image } from '~/modules/page-builder/type/message-passing';
import { useAppData } from '~/modules/root';
import type { CreateMarkerAction } from '~/routes/remix-api.product-tagging.create-marker';
import { useToaster } from '~/modules/root/context/ToasterContext';
import generateFetcherKey from '~/utils/generate-fetcher-key';
import { fetcherIsDone } from '~/utils/fetcherHelper';

interface CreateTagProps {
    data: TagDataUseToCreateTag;
    libraryImage: Image;
    tagPosition: TagPosition;
}

const useCreateTag = (
    canDeleteTag: boolean,
): {
    createTag: ({ data, libraryImage, tagPosition }: CreateTagProps) => void;
} => {
    const fetcher = useFetcher<CreateMarkerAction>();
    const { user } = useAppData();
    const { toast } = useToaster({ autoHideDelay: 3 });

    const { dispatch } = useProductTagging();

    useEffect(() => {
        if (fetcher.state === 'loading') {
            toast('Tag saved');
        }
        if (fetcherIsDone(fetcher) && fetcher.data?.createLibraryImageMarker) {
            dispatch({ type: 'ADDING_TAG', payload: undefined });
        }
    }, [fetcher, toast, dispatch]);

    const createTag = ({ data, libraryImage, tagPosition }: CreateTagProps) => {
        if (tagPosition) {
            const formData = new FormData();
            formData.set('libraryImageID', libraryImage.ID.toString());

            const {
                CropPosX,
                CropPosY,
                WidthBeforeCrop,
                WidthAfterCrop,
                HeightBeforeCrop,
                HeightAfterCrop,
            } = libraryImage;

            const offsetPosX =
                getPixelValue(tagPosition.pctX, WidthAfterCrop) + CropPosX;
            const offsetPosY =
                getPixelValue(tagPosition.pctY, HeightAfterCrop) + CropPosY;

            const offsetPctX = getPercentageValue(offsetPosX, WidthBeforeCrop);
            const offsetPctY = getPercentageValue(offsetPosY, HeightBeforeCrop);

            const targetID = data?.product
                ? data.product.id
                : data?.professionalForUnknownProduct?.id;

            formData.set('positionX', offsetPctX.toString());
            formData.set('positionY', offsetPctY.toString());
            if (targetID) {
                formData.set('targetID', targetID.toString());
            }
            formData.set('fetcherKey', generateFetcherKey());

            const unknownProfessional =
                data.titleForUnknownProfessional?.trim();
            if (unknownProfessional) {
                formData.set('professionalName', unknownProfessional);
            }

            if (data.titleForUnknownProduct) {
                formData.set(
                    'titleForUnknownProduct',
                    data.titleForUnknownProduct,
                );
            }
            if (data.urlForUnknownProduct) {
                formData.set('urlForUnknownProduct', data.urlForUnknownProduct);
            }

            // keep record of the latest tag saved
            dispatch({
                type: 'ADDING_LAST_ADDED_TAG',
                payload: {
                    pctX: roundToTwo(offsetPctX),
                    pctY: roundToTwo(offsetPctY),
                    libraryImageID: libraryImage.ID,
                },
            });

            const isTagApproved =
                data.titleForUnknownProfessional ||
                data.professionalForUnknownProduct
                    ? false
                    : canDeleteTag;

            //push optimistic data
            const optimisticData: TagData = {
                ...data,
                id: new Date().getUTCMilliseconds(), // its random enough
                positionX: roundToTwo(offsetPctX),
                positionY: roundToTwo(offsetPctY),
                approved: isTagApproved,
                isOptimistic: true,
            };

            if (user?.__typename === 'Me') {
                optimisticData.member = {
                    email: user.Email,
                    firstName: user.FirstName,
                    lastName: user.LastName,
                };
            }

            formData.set('optimisticData', JSON.stringify(optimisticData));

            fetcher.submit(formData, {
                method: 'post',
                action: '/remix-api/product-tagging/create-marker',
                preventScrollReset: true,
            });
        }
    };

    return {
        createTag,
    };
};

export default useCreateTag;
